// =============================================
// فرق الكلمات بين النصّ الخام والنصّ المنسّق
// =============================================
//
// النصّان محفوظان معاً، لكن لا شيء يُظهر ما أحدثه التنسيق الآلي في كلام
// الشيخ. المقارنة على صورة مطبَّعة (بلا تشكيل ولا ترقيم، والهمزات والألفات
// والياءات والتاء المربوطة مردودة إلى أصل واحد) حتى لا يصير كل تشكيل
// أضافه النموذج «تغييراً»، فيغرق الحقيقيّ في الضجيج.
//
// نظير `App\Support\ArabicText::comparisonKey` في الخادم — أي تعديل هنا
// يلزمه نظيره هناك.

const DIACRITICS = /[ؐ-ًؚ-ٰٟۖ-ۭـ]/g;
const NON_WORD = /[^\p{Letter}\p{Number}\s]/gu;

const FOLD = {
  'أ': 'ا', 'إ': 'ا', 'آ': 'ا', 'ٱ': 'ا', 'ٲ': 'ا', 'ٳ': 'ا',
  'ى': 'ي', 'ئ': 'ي', 'ؤ': 'و', 'ء': '',
  'ة': 'ه',
  'ـ': '',
};

export function normalizeArabic(text) {
  return String(text)
    .replace(DIACRITICS, '')
    .replace(/[أإآٱٲٳىئؤءةـ]/g, ch => FOLD[ch])
    .replace(NON_WORD, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** يقصّ النصّ إلى كلمات محتفظاً بصورتها الأصلية إلى جانب مفتاح المقارنة. */
function tokenize(text) {
  return String(text || '')
    .split(/\s+/)
    .filter(Boolean)
    .map(word => ({ word, key: normalizeArabic(word) }))
    .filter(t => t.key !== '');
}

/**
 * فرق كلمة بكلمة بين نصّين شبه متطابقين.
 *
 * ليس LCS كاملاً: التنسيق لا يعيد ترتيب الكلام، إنما يضيف ويحذف قليلاً هنا
 * وهناك — فمؤشّران يمشيان معاً، وعند أوّل اختلاف يُبحث عن أقرب نقطة تزامن
 * ضمن نافذة محدودة. هذا خطّي في طول النصّ، بينما LCS على تفريغ درس (عشرات
 * آلاف الكلمات) تربيعيّ فيُجمّد المتصفّح.
 *
 * @returns {{type: 'same'|'add'|'del', text: string}[]}
 */
export function diffWords(rawText, formattedText, lookahead = 60) {
  const before = tokenize(rawText);
  const after = tokenize(formattedText);

  const parts = [];
  const push = (type, words) => {
    if (!words.length) return;
    const last = parts[parts.length - 1];
    if (last && last.type === type) last.text += ' ' + words.join(' ');
    else parts.push({ type, text: words.join(' ') });
  };

  let i = 0;
  let j = 0;

  while (i < before.length && j < after.length) {
    if (before[i].key === after[j].key) {
      push('same', [after[j].word]);
      i++; j++;
      continue;
    }

    const sync = findResync(before, after, i, j, lookahead);
    if (!sync) break;

    push('del', before.slice(i, sync.i).map(t => t.word));
    push('add', after.slice(j, sync.j).map(t => t.word));
    i = sync.i;
    j = sync.j;
  }

  push('del', before.slice(i).map(t => t.word));
  push('add', after.slice(j).map(t => t.word));

  return parts;
}

/**
 * أقرب موضع تعود عنده الكلمات إلى التطابق. يُفضَّل ما قلّ مجموع تخطّيه على
 * الجانبين، فلا يُبتلع مقطع سليم لمجرّد أنه سبق نقطة تزامن أبعد.
 */
function findResync(before, after, i, j, lookahead) {
  for (let span = 1; span <= lookahead; span++) {
    for (let a = 0; a <= span; a++) {
      const b = span - a;
      const bi = i + a;
      const aj = j + b;
      if (bi >= before.length || aj >= after.length) continue;
      if (before[bi].key === after[aj].key) return { i: bi, j: aj };
    }
  }

  return null;
}

/** عدّاد سريع لرأس اللوحة: كم كلمة أُضيفت وكم حُذفت. */
export function diffStats(parts) {
  const count = type => parts
    .filter(p => p.type === type)
    .reduce((n, p) => n + p.text.split(/\s+/).filter(Boolean).length, 0);

  return { added: count('add'), removed: count('del') };
}
